/**
 * Endereços de um dono — o painel onde a correlação por "moram no mesmo lugar" nasce.
 *
 * Fluxo do CEP: digita o CEP (máscara `99999-999`, só dígitos no banco), o sistema completa o
 * endereço (ViaCEP) e leva o foco ao **número** se achou a rua, ou à **rua** se não achou. IBGE e
 * coordenadas vêm junto e são gravados no cadastro — todo endereço nasce georreferenciado. O
 * endereço é deduplicado no banco: se já existe, só criamos o **vínculo** (com período), não
 * recadastramos — e o selo mostra na hora quem mais mora ali. O vínculo tem vigência editável.
 */
import { Map, MapPin, Pencil, Star, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CampoDocumento } from '@/components/CampoDocumento';
import { BadgeCorrelacao } from '@/components/crud/BadgeCorrelacao';
import { BuscaEnderecoGoogle, type EnderecoEscolhido } from '@/components/crud/BuscaEnderecoGoogle';
import { DrawerAdicionar } from '@/components/crud/DrawerAdicionar';
import { ModalMapa } from '@/components/crud/ModalMapa';
import { Button } from '@/components/ui/button';
import { estadoDaLista } from '@/components/ui/estado';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Chip, Spinner } from '@/components/ui/misc';
import { api, consultarCep, type ContatoEnderecoCad, type CorrelacaoCad } from '@/lib/api';
import { useCorrelacoes } from '@/lib/correlacoes';
import type { OwnerTipo } from '@/lib/recursos/tipos';

const CLASSIF_ENDERECO = ['residencial', 'comercial', 'cobranca', 'entrega', 'fiscal'];

const CHAVE_MAPS = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

const VAZIO = {
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: '',
  cep: '',
  ibge: '' as string | null,
  latitude: '' as string | null,
  longitude: '' as string | null,
};

function linha(en: ContatoEnderecoCad['endereco']): string {
  const p1 = [en.logradouro, en.numero].filter(Boolean).join(', ');
  const p2 = [en.bairro, en.cidade, en.uf].filter(Boolean).join(' · ');
  return [p1, p2].filter(Boolean).join(' — ');
}

function periodo(inicio: string | null, fim: string | null): string | null {
  if (!inicio && !fim) return null;
  const fmt = (d: string) => d.split('-').reverse().join('/');
  if (inicio && fim) return `${fmt(inicio)} – ${fmt(fim)}`;
  if (inicio) return `desde ${fmt(inicio)}`;
  return `até ${fmt(fim as string)}`;
}

export function PainelEnderecos({ ownerTipo, ownerId }: { ownerTipo: OwnerTipo; ownerId: string }) {
  const url = `/cadastro/${ownerTipo}/${ownerId}/enderecos`;
  const [itens, setItens] = useState<ContatoEnderecoCad[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [form, setForm] = useState({ ...VAZIO });
  const [classificacao, setClassificacao] = useState('residencial');
  const [principal, setPrincipal] = useState(false);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [editando, setEditando] = useState<string | null>(null); // `${id}/${classificacao}`
  const [mapaDe, setMapaDe] = useState<ContatoEnderecoCad['endereco'] | null>(null);

  const numeroRef = useRef<HTMLInputElement>(null);
  const logradouroRef = useRef<HTMLInputElement>(null);

  const correl = useCorrelacoes(
    'endereco',
    itens.map((e) => e.endereco.id),
  );

  const set = (k: keyof typeof VAZIO, v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function carregar() {
    const r = await api.get<ContatoEnderecoCad[]>(url);
    setCarregando(false);
    if (r.ok) setItens(r.dados);
  }
  useEffect(() => {
    setCarregando(true);
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  /** CEP completo (8 dígitos) → completa o endereço e leva o foco ao próximo campo natural. */
  async function aoMudarCep(cepDigitos: string) {
    set('cep', cepDigitos);
    if (cepDigitos.length !== 8) return;
    setBuscandoCep(true);
    const d = await consultarCep(cepDigitos);
    setBuscandoCep(false);
    if (!d) return;
    setForm((f) => ({
      ...f,
      cep: cepDigitos,
      logradouro: d.logradouro ?? f.logradouro,
      bairro: d.bairro ?? f.bairro,
      cidade: d.cidade ?? f.cidade,
      uf: d.uf ?? f.uf,
      ibge: d.ibge,
      latitude: d.latitude,
      longitude: d.longitude,
    }));
    // Achou a rua → o que falta é o número. Não achou → o operador digita a rua.
    if (d.logradouro) numeroRef.current?.focus();
    else logradouroRef.current?.focus();
  }

  /** Sugestão do Google escolhida → preenche o que veio e leva o foco ao número (o que costuma
   * faltar). IBGE não vem do Places; o enriquecimento no salvar o completa pelo CEP. */
  function aoEscolherGoogle(e: EnderecoEscolhido) {
    setForm((f) => ({
      ...f,
      logradouro: e.logradouro ?? f.logradouro,
      numero: e.numero ?? f.numero,
      bairro: e.bairro ?? f.bairro,
      cidade: e.cidade ?? f.cidade,
      uf: e.uf ?? f.uf,
      cep: e.cep ? e.cep.replace(/\D/g, '') : f.cep,
      latitude: e.latitude ?? f.latitude,
      longitude: e.longitude ?? f.longitude,
    }));
    numeroRef.current?.focus();
  }

  function limparForm() {
    setForm({ ...VAZIO });
    setClassificacao('residencial');
    setPrincipal(false);
    setDataInicio('');
    setDataFim('');
  }

  async function adicionar(): Promise<boolean> {
    if (!form.logradouro.trim() || !form.cidade.trim() || !form.uf.trim() || salvando) return false;
    setSalvando(true);
    const r = await api.post<{ correlacoes?: { total: number } }>(url, {
      endereco: {
        logradouro: form.logradouro.trim(),
        numero: form.numero.trim() || null,
        complemento: form.complemento.trim() || null,
        bairro: form.bairro.trim() || null,
        cidade: form.cidade.trim(),
        uf: form.uf.trim().toUpperCase(),
        cep: form.cep.trim() || null,
        ibge: form.ibge || null,
        latitude: form.latitude || null,
        longitude: form.longitude || null,
      },
      classificacao,
      principal,
      data_inicio: dataInicio || null,
      data_fim: dataFim || null,
    });
    setSalvando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return false;
    }
    // Conexão proativa: se o endereço já era de outra pessoa, avisa na hora.
    const total = r.dados?.correlacoes?.total ?? 0;
    if (total >= 2) toast.success(`Endereço vinculado — e ligado a ${total} cadastros. Veja o selo.`);
    else toast.success('Endereço vinculado.');
    limparForm();
    void carregar();
    return true;
  }

  async function remover(en: ContatoEnderecoCad) {
    const r = await api.del(`${url}/${en.endereco.id}/${en.classificacao}`);
    if (!r.ok) return void toast.error(r.erro);
    void carregar();
  }

  async function salvarEdicao(en: ContatoEnderecoCad, dados: EdicaoVinculo) {
    const r = await api.patch(`${url}/${en.endereco.id}/${en.classificacao}`, {
      // Trocar a classificação re-cria o vínculo no backend (está na PK), preservando período.
      nova_classificacao: dados.classificacao !== en.classificacao ? dados.classificacao : undefined,
      principal: dados.principal,
      data_inicio: dados.data_inicio || null,
      data_fim: dados.data_fim || null,
    });
    if (!r.ok) return void toast.error(r.erro);
    setEditando(null);
    toast.success('Vínculo atualizado.');
    void carregar();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="size-4 text-accent" />
          Endereços
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {estadoDaLista(carregando, itens.length === 0, 'Nenhum endereço vinculado.') ?? (
          <div className="flex flex-col gap-2">
            {itens.map((en) => {
              const chave = `${en.endereco.id}/${en.classificacao}`;
              return editando === chave ? (
                <FormEdicao
                  key={chave}
                  inicial={en}
                  onCancelar={() => setEditando(null)}
                  onSalvar={(d) => void salvarEdicao(en, d)}
                />
              ) : (
                <ItemEndereco
                  key={chave}
                  en={en}
                  correlacao={correl[en.endereco.id]}
                  onVerMapa={() => setMapaDe(en.endereco)}
                  onEditar={() => setEditando(chave)}
                  onRemover={() => void remover(en)}
                />
              );
            })}
          </div>
        )}

        <DrawerAdicionar
          titulo="Novo endereço"
          descricao="Digite o CEP e o endereço se completa. Se já existir na base, apenas o correlacionamos."
          rotuloAbrir="Adicionar endereço"
          podeSalvar={!!form.logradouro.trim() && !!form.cidade.trim() && !!form.uf.trim()}
          salvando={salvando}
          onSalvar={adicionar}
          onFechar={limparForm}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
            {CHAVE_MAPS && (
              <div className="sm:col-span-6">
                <BuscaEnderecoGoogle apiKey={CHAVE_MAPS} onEscolher={aoEscolherGoogle} />
              </div>
            )}
            <div className="sm:col-span-2">
              <CampoDocumento rotulo="CEP" valor={form.cep} onChange={(v) => void aoMudarCep(v)} formato="cep" />
              {buscandoCep && (
                <span className="tipo-corpo-pequeno mt-1 flex items-center gap-1 text-text-tertiary">
                  <Spinner className="size-3" /> buscando…
                </span>
              )}
            </div>
            <Input
              ref={logradouroRef}
              className="self-end sm:col-span-4"
              placeholder="Logradouro"
              value={form.logradouro}
              onChange={(e) => set('logradouro', e.target.value)}
            />
            <Input
              ref={numeroRef}
              className="sm:col-span-2"
              placeholder="Número"
              value={form.numero}
              onChange={(e) => set('numero', e.target.value)}
            />
            <Input className="sm:col-span-4" placeholder="Complemento" value={form.complemento} onChange={(e) => set('complemento', e.target.value)} />
            <Input className="sm:col-span-2" placeholder="Bairro" value={form.bairro} onChange={(e) => set('bairro', e.target.value)} />
            <Input className="sm:col-span-3" placeholder="Cidade" value={form.cidade} onChange={(e) => set('cidade', e.target.value)} />
            <Input className="sm:col-span-1" placeholder="UF" maxLength={2} value={form.uf} onChange={(e) => set('uf', e.target.value)} />
          </div>
          {(form.ibge || form.latitude) && (
            <p className="tipo-corpo-pequeno text-text-tertiary">
              {form.ibge && <>IBGE {form.ibge}</>}
              {form.latitude && form.longitude && (
                <> · {Number(form.latitude).toFixed(5)}, {Number(form.longitude).toFixed(5)}</>
              )}
            </p>
          )}
          <div className="flex flex-wrap items-end gap-3">
            <SelectClassif valor={classificacao} onChange={setClassificacao} />
            <CampoData rotulo="Desde" valor={dataInicio} onChange={setDataInicio} />
            <CampoData rotulo="Até" valor={dataFim} onChange={setDataFim} />
            <CheckPrincipal valor={principal} onChange={setPrincipal} />
          </div>
        </DrawerAdicionar>
      </CardContent>
      <ModalMapa endereco={mapaDe} onFechar={() => setMapaDe(null)} />
    </Card>
  );
}

function ItemEndereco({
  en,
  correlacao,
  onVerMapa,
  onEditar,
  onRemover,
}: {
  en: ContatoEnderecoCad;
  correlacao?: CorrelacaoCad;
  onVerMapa: () => void;
  onEditar: () => void;
  onRemover: () => void;
}) {
  const vig = periodo(en.data_inicio, en.data_fim);
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2">
      <div className="min-w-0 flex-1">
        <span className="text-[15px] text-text">{linha(en.endereco)}</span>
        {(vig || en.endereco.ibge) && (
          <span className="tipo-corpo-pequeno mt-0.5 block text-text-tertiary">
            {vig}
            {vig && en.endereco.ibge && ' · '}
            {en.endereco.ibge && `IBGE ${en.endereco.ibge}`}
          </span>
        )}
      </div>
      <BadgeCorrelacao correlacao={correlacao} />
      <Chip>{en.classificacao}</Chip>
      {en.principal && (
        <Chip aceso>
          <Star className="mr-1 size-3" /> principal
        </Chip>
      )}
      <Button variant="fantasma" size="sm" onClick={onVerMapa} aria-label="Ver no mapa" title="Ver no mapa">
        <Map />
      </Button>
      <Button variant="fantasma" size="sm" onClick={onEditar} aria-label="Editar vínculo">
        <Pencil />
      </Button>
      <Button variant="fantasma" size="sm" className="text-danger hover:text-danger" onClick={onRemover} aria-label="Desvincular">
        <Trash2 />
      </Button>
    </div>
  );
}

type EdicaoVinculo = {
  classificacao: string;
  principal: boolean;
  data_inicio: string;
  data_fim: string;
};

function FormEdicao({
  inicial,
  onSalvar,
  onCancelar,
}: {
  inicial: ContatoEnderecoCad;
  onSalvar: (d: EdicaoVinculo) => void;
  onCancelar: () => void;
}) {
  const [classificacao, setClassificacao] = useState(inicial.classificacao);
  const [principal, setPrincipal] = useState(inicial.principal);
  const [dataInicio, setDataInicio] = useState(inicial.data_inicio ?? '');
  const [dataFim, setDataFim] = useState(inicial.data_fim ?? '');
  return (
    <div className="flex flex-col gap-3 rounded-md border border-border-strong bg-surface-2 px-3 py-3">
      <span className="tipo-rotulo text-text-secondary">{linha(inicial.endereco)}</span>
      <div className="flex flex-wrap items-end gap-3">
        <SelectClassif valor={classificacao} onChange={setClassificacao} />
        <CampoData rotulo="Desde" valor={dataInicio} onChange={setDataInicio} />
        <CampoData rotulo="Até" valor={dataFim} onChange={setDataFim} />
        <CheckPrincipal valor={principal} onChange={setPrincipal} />
        <Button
          type="button"
          variant="secundario"
          onClick={() => onSalvar({ classificacao, principal, data_inicio: dataInicio, data_fim: dataFim })}
        >
          Salvar
        </Button>
        <Button type="button" variant="fantasma" onClick={onCancelar} aria-label="Cancelar">
          <X />
        </Button>
      </div>
    </div>
  );
}

function SelectClassif({ valor, onChange }: { valor: string; onChange: (v: string) => void }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="tipo-rotulo">Classificação</span>
      <select
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
      >
        {CLASSIF_ENDERECO.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
    </label>
  );
}

function CampoData({
  rotulo,
  valor,
  onChange,
}: {
  rotulo: string;
  valor: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-2">
      <span className="tipo-rotulo">{rotulo}</span>
      <input
        type="date"
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
      />
    </label>
  );
}

function CheckPrincipal({ valor, onChange }: { valor: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex h-10 cursor-pointer items-center gap-2">
      <input type="checkbox" checked={valor} onChange={(e) => onChange(e.target.checked)} className="size-4 accent-[var(--primary)]" />
      <span className="text-[15px] text-text">Principal</span>
    </label>
  );
}
